import { View } from 'react-native';
import { Button, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import type { DriverWorkState } from './types';

export interface WorkStateActionHandlers {
  onStartSession: () => void;
  onEndSession: () => void;
  onStartDriving: () => void;
  onStopDriving: () => void;
}

const LABEL = { paddingVertical: 8, fontSize: 16 } as const;

/**
 * Bottom action bar of the driver home: the same buttons, rules, colours and
 * sizes as DashboardContent today. `stacked` is the phone layout (full-width
 * buttons, one per row).
 */
export const WorkStateActions = ({
  state,
  stacked,
  disabled = false,
  handlers,
}: {
  state: DriverWorkState;
  stacked: boolean;
  disabled?: boolean;
  handlers: WorkStateActionHandlers;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const width = stacked ? ('100%' as const) : undefined;

  if (state === 'resting') {
    return (
      <Button
        testID="work-action-start-session"
        mode="contained"
        onPress={handlers.onStartSession}
        disabled={disabled}
        style={{ borderRadius: 16, backgroundColor: theme.colors.tertiary, width: '100%' }}
        labelStyle={{ ...LABEL, color: theme.colors.onTertiary }}
        contentStyle={{ paddingHorizontal: 16 }}
      >
        {t('startSession')}
      </Button>
    );
  }

  const driving = state === 'driving';
  return (
    <View style={{ flexDirection: stacked ? 'column' : 'row', gap: 10 }}>
      <Button
        testID="work-action-end-session"
        mode="contained"
        onPress={handlers.onEndSession}
        // As today: the session can only end when not driving.
        disabled={disabled || driving}
        style={{
          flex: stacked ? undefined : 1,
          width,
          borderRadius: 16,
          backgroundColor: theme.colors.error,
          opacity: driving ? 0.5 : 1,
        }}
        labelStyle={{ ...LABEL, color: theme.colors.onError }}
        contentStyle={{ paddingHorizontal: 16 }}
      >
        {t('endSession')}
      </Button>
      <Button
        testID={driving ? 'work-action-stop-driving' : 'work-action-start-driving'}
        mode="contained"
        onPress={driving ? handlers.onStopDriving : handlers.onStartDriving}
        disabled={disabled}
        style={{ flex: stacked ? undefined : 1, width, borderRadius: 16, backgroundColor: theme.colors.tertiary }}
        labelStyle={{ ...LABEL, color: theme.colors.onTertiary }}
        contentStyle={{ paddingHorizontal: 16 }}
      >
        {driving ? t('stopDriving') : t('startDriving')}
      </Button>
    </View>
  );
};
